import React, { useContext, useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { toast } from 'sonner';
import { I18nContext } from '../context/translations/TranslationsContext.context';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from './ui/alert-dialog';
import {
	closeDemoAccountNotice,
	DEMO_ACCOUNT_BLOCKED_EVENT
} from '../utils/demoAccountInterceptor';

/**
 * Mientras el aviso está abierto, `index.css` oculta los toasts con este atributo en `<html>`:
 * la pantalla que hizo la operación pinta además su propio error y saldrían dos mensajes.
 */
export const DEMO_ACCOUNT_NOTICE_ATTRIBUTE = 'data-demo-account-notice';

/**
 * Al cerrar, los toasts se descartan YA, pero la marca se quita después de su animación de
 * salida (200 ms en sonner): si no, se verían irse por debajo del aviso que se cierra.
 */
export const TOAST_EXIT_MS = 400;

/**
 * Aviso ÚNICO de la cuenta demo de los revisores de las tiendas (comparsa
 * `docs/CUENTAS_DEMO.md`). Se monta una vez en `App` y se abre cuando el backend bloquea
 * una operación de la cuenta demo (lo detecta `installDemoAccountInterceptor`). Ninguna
 * pantalla tiene que tratar el bloqueo por su cuenta. Para los clientes reales no se abre
 * nunca: el backend no les marca ninguna respuesta.
 *
 * Es un `AlertDialog` (una sola acción, sin la X de `ui/dialog`, que dice «Cerrar» en
 * cualquier idioma). Se cierra con «Entendido» o con Escape.
 */
const DemoAccountBlockedNotice: React.FC = () => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const [open, setOpen] = useState(false);
	const unmarkTimer = useRef<number>();

	useEffect(() => {
		const show = () => {
			// La marca se pone YA, en el mismo instante del bloqueo: la pantalla saca su toast en
			// cuanto recibe la respuesta, antes de que React pinte el aviso.
			window.clearTimeout(unmarkTimer.current);
			document.documentElement.setAttribute(DEMO_ACCOUNT_NOTICE_ATTRIBUTE, 'open');
			setOpen(true);
		};
		window.addEventListener(DEMO_ACCOUNT_BLOCKED_EVENT, show);
		return () => window.removeEventListener(DEMO_ACCOUNT_BLOCKED_EVENT, show);
	}, []);

	useEffect(() => {
		if (!open) return;
		return () => {
			closeDemoAccountNotice();
			// Los toasts de error que la pantalla sacó mientras tanto no deben aparecer después.
			toast.dismiss();
			unmarkTimer.current = window.setTimeout(() => {
				// Y otra vez: un toast que la pantalla sacara justo después de cerrar tampoco sale.
				toast.dismiss();
				document.documentElement.removeAttribute(DEMO_ACCOUNT_NOTICE_ATTRIBUTE);
			}, TOAST_EXIT_MS);
		};
	}, [open]);

	// Al desmontarse (va DESPUÉS del efecto anterior: su limpieza corre antes) no deja ni la
	// marca ni el temporizador vivos.
	useEffect(
		() => () => {
			window.clearTimeout(unmarkTimer.current);
			document.documentElement.removeAttribute(DEMO_ACCOUNT_NOTICE_ATTRIBUTE);
		},
		[]
	);

	return (
		<AlertDialog open={open} onOpenChange={setOpen}>
			<AlertDialogContent className="sm:max-w-md">
				<AlertDialogHeader>
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
							<Info className="w-5 h-5 text-primary" />
						</div>
						<AlertDialogTitle>{translate('demoAccount.blocked.title')}</AlertDialogTitle>
					</div>
					<AlertDialogDescription>{translate('demoAccount.blocked.body')}</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogAction>{translate('demoAccount.blocked.understood')}</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};

export default DemoAccountBlockedNotice;
